import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import Table from "../../../../ui-components/table/table";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import XGSErrorMessage from "../../../../ui-components/error-message/errorMessage";
import { userSelector } from "../../../../slices/user/userSlice";
import {
  pickupAssignmentSelector
} from "../../../../slices/pickup/pickupAssignmentSlice";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import {
  PickupsLogFilterSchema,
  CustomerViewPickupSummaryFilterListModel,
  TIME_PERIOD_LIST
} from "../../../../app/data/pickup/models";
import { PicklistsSummary } from "./pickup-summary/pickupSummary";
import "./customerViewPickup.scss";
import { useCustomerViewPickupSummary, useCustomerViewPickupSummaryTiles, useRequesterCompany } from "../../../../hooks/usePickupSummary";
import CustomerViewPickupDetails from "./CustomerViewDetails/customerViewPickupDetails";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";

interface TerminalViewPickupSummaryProps {
  filterRefCustomer?: React.MutableRefObject<any>;
}

const columnHelper = createColumnHelper<any>();

const CustomerViewPickupSummary: React.FC<TerminalViewPickupSummaryProps> = ({ filterRefCustomer }) => {
  const userState = useSelector(userSelector);
  const currentTerminalCodeFetch = userState.activeTerminal?.id;

  const initialValues: CustomerViewPickupSummaryFilterListModel = {
    currentTerminalCode: currentTerminalCodeFetch,
    timePeriod: "THIS_WEEK",
    from: "",
    to: "",
    requesterCompanyIds: [],
  };

  const getDateRangeForTimePeriod = (timePeriod: string) => {
    const today = moment();

    switch (timePeriod) {
      case "THIS_WEEK":
        return {
          from: today.clone().startOf("week").toDate(), // Sunday
          to: today.clone().endOf("week").toDate(),     // Saturday
        };
      case "LAST_WEEK":
        return {
          from: today.clone().subtract(1, "week").startOf("week").toDate(),
          to: today.clone().subtract(1, "week").endOf("week").toDate(),
        };
      case "THIS_MONTH":
        return {
          from: today.clone().startOf("month").toDate(),
          to: today.clone().endOf("month").toDate(),
        };
      case "LAST_MONTH":
        return {
          from: today.clone().subtract(1, "month").startOf("month").toDate(),
          to: today.clone().subtract(1, "month").endOf("month").toDate(),
        };
      case "THIS_QUARTER":
        return {
          from: today.clone().startOf("quarter").toDate(),
          to: today.clone().endOf("quarter").toDate(),
        };
      case "LAST_QUARTER":
        return {
          from: today.clone().subtract(1, "quarter").startOf("quarter").toDate(),
          to: today.clone().subtract(1, "quarter").endOf("quarter").toDate(),
        };
      case "CUSTOM":
      default:
        return { from: undefined, to: undefined };
    }
  };

  const initializeFilter = (): CustomerViewPickupSummaryFilterListModel => {
    const defaultFilter = { ...initialValues };

    if (defaultFilter.timePeriod === "THIS_WEEK") {
      const { from, to } = getDateRangeForTimePeriod("THIS_WEEK");
      defaultFilter.from = from?.toISOString() ?? undefined;
      defaultFilter.to = to?.toISOString() ?? undefined;
    }

    return defaultFilter;
  };


  const [filter, setFilter] = useState<CustomerViewPickupSummaryFilterListModel>(initializeFilter());
  const { data: originterminalOptions = [] } = useRequesterCompany();
  const [customerViewNumber, setCustomerViewNumber] = useState<string | null>(null);
  const [popupFilterData, setPopupFilterData] = useState<CustomerViewPickupSummaryFilterListModel>({});

  const currentTerminalCode = useMemo(() => {
    if (!userState.activeTerminal?.id) return 0;
    return userState.activeTerminal.id;
  }, [userState.activeTerminal?.id]);


  const pickupAssignmentState = useSelector(pickupAssignmentSelector);
  const filterFormRef = useRef<FormikProps<CustomerViewPickupSummaryFilterListModel>>(null);


  const onFilterSubmit = (data: CustomerViewPickupSummaryFilterListModel) => {
    const terminals: XGSSelectOption[] = Array.isArray(data.requesterCompanyIds) ? data.requesterCompanyIds : [];

    const formattedFilterData = {
      ...data,
      from: data.from?.toApiDateFormat(),
      to: data.to?.toApiDateFormat(),
      requesterCompanyIds: terminals,
    };
    setPopupFilterData(formattedFilterData);

    setFilter({
      ...data,
      requesterCompanyIds: terminals,
    });

    refetchList(formattedFilterData);
    refetchTiles(formattedFilterData);
  };

  useEffect(() => {
    if (filter.timePeriod === "THIS_WEEK") {
      onFilterSubmit(filter);
    }
    // eslint-disable-next-line
  }, []);

  const pickupSummaryFilter = useMemo(() => {
    if (!popupFilterData || Object.keys(popupFilterData).length === 0) {

      return {
        currentTerminalCode,
        timePeriod: filter.timePeriod || "CUSTOM",
        from: filter.from ? filter.from.toApiDateFormat() : "",
        to: filter.to ? filter.to.toApiDateFormat() : "",
        requesterCompanyIds: Array.isArray(filter.requesterCompanyIds) && filter.requesterCompanyIds.length > 0
          ? filter.requesterCompanyIds.map(t => t.value).join(",")
          : "",
      };
    }

    const isCustom = (popupFilterData?.timePeriod || filter.timePeriod) === "CUSTOM";

    return {
      ...popupFilterData,
      requesterCompanyIds: Array.isArray(filter.requesterCompanyIds) && filter.requesterCompanyIds.length > 0
        ? filter.requesterCompanyIds.map(t => t.value).join(",")
        : "",
      from: isCustom ? popupFilterData.from?.toApiDateFormat() : undefined,
      to: isCustom ? popupFilterData.to?.toApiDateFormat() : undefined,
    };
  }, [popupFilterData, currentTerminalCode,
    filter.from,
    filter.to,
    filter.timePeriod,
    filter.requesterCompanyIds]);

  const {
    data: pickupSummaryList,
    isLoading: listLoading,
    refetch: refetchList
  } = useCustomerViewPickupSummary(pickupSummaryFilter, { enabled: !!currentTerminalCode });

  const {
    data: pickupSummaryTiles,
    isLoading: tilesLoading,
    refetch: refetchTiles
  } = useCustomerViewPickupSummaryTiles(pickupSummaryFilter, { enabled: !!currentTerminalCode });

  const onShowViewDetails = (terminalNum: string, status?: string) => {
    setCustomerViewNumber(terminalNum);
    setPopupFilterData((prev) => ({ ...prev, status }));
  };

  useEffect(() => {
    if (filterRefCustomer) {
      filterRefCustomer.current = pickupSummaryFilter;
    }
  }, [pickupSummaryFilter, filterRefCustomer]);

  const columns = [
    columnHelper.accessor("requesterCompany", {
      id: "requesterCompany",
      header: "Requester (Company)",
      size: 100,
    }),
    columnHelper.accessor("created", {
      id: "created",
      header: "Created",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.requesterCompanyId, "Created");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("completed", {
      id: "completed",
      header: "Completed",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.requesterCompanyId, "Completed");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("missed", {
      id: "missed",
      header: "Missed",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.requesterCompanyId, "Missed");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("rescheduled", {
      id: "rescheduled",
      header: "Rescheduled",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.requesterCompanyId, "Rescheduled");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("cancelled", {
      id: "cancelled",
      header: "Canceled",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.requesterCompanyId, "Canceled");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),

  ];

  const getTimePeriodFromDates = (from?: string, to?: string): string => {
    if (!from || !to) return "CUSTOM";

    const fromDate = moment(from);
    const toDate = moment(to);
    const today = moment();

    if (
      fromDate.isSame(today.clone().startOf("week"), "day") &&
      toDate.isSame(today.clone().endOf("week"), "day")
    ) {
      return "THIS_WEEK";
    }

    if (
      fromDate.isSame(today.clone().subtract(1, "week").startOf("week"), "day") &&
      toDate.isSame(today.clone().subtract(1, "week").endOf("week"), "day")
    ) {
      return "LAST_WEEK";
    }

    if (
      fromDate.isSame(today.clone().startOf("month"), "day") &&
      toDate.isSame(today.clone().endOf("month"), "day")
    ) {
      return "THIS_MONTH";
    }

    if (
      fromDate.isSame(today.clone().subtract(1, "month").startOf("month"), "day") &&
      toDate.isSame(today.clone().subtract(1, "month").endOf("month"), "day")
    ) {
      return "LAST_MONTH";
    }

    if (
      fromDate.isSame(today.clone().startOf("quarter"), "day") &&
      toDate.isSame(today.clone().endOf("quarter"), "day")
    ) {
      return "THIS_QUARTER";
    }

    if (
      fromDate.isSame(today.clone().subtract(1, "quarter").startOf("quarter"), "day") &&
      toDate.isSame(today.clone().subtract(1, "quarter").endOf("quarter"), "day")
    ) {
      return "LAST_QUARTER";
    }

    return "CUSTOM";
  };



  const getTimePeriodListValues = () => {
    return Object.keys(TIME_PERIOD_LIST).map((key: string) => {
      return {
        value: key,
        label: TIME_PERIOD_LIST[key as keyof typeof TIME_PERIOD_LIST]
      };
    });
  };

  const getOriginTerminalOptions = (selectedOptions: XGSSelectOption[] = []) => {
    const selectedValues = selectedOptions.map((opt) => opt.value);

    const flatList = Array.isArray(originterminalOptions)
      ? originterminalOptions.flat()
      : originterminalOptions;

    return flatList
      .filter((item) => !selectedValues.includes(item.value))
      .map((item) => ({
        label: item.label,
        value: item.value,
      }));
  };


  const handleTimePeriodChange = (
    timePeriodOption: XGSSelectOption | null,
    props: FormikProps<CustomerViewPickupSummaryFilterListModel>
  ) => {
    const timePeriod = timePeriodOption?.value?.toString() ?? undefined;

    props.setFieldValue("timePeriod", timePeriod);

    const { from, to } = getDateRangeForTimePeriod(timePeriod ?? "CUSTOM");

    const fromDateStr = from?.toISOString();
    const toDateStr = to?.toISOString();

    props.setFieldValue("from", fromDateStr);
    props.setFieldValue("to", toDateStr);

    onFilterSubmit({
      ...props.values,
      timePeriod,
      from: fromDateStr,
      to: toDateStr,
    });
  };

  const clearSearchRecords = () => {
    filterFormRef.current?.resetForm({
      values: initializeFilter(),
    });
    setFilter(initializeFilter());
  };

  return (
    <>
      <div className="xgs-customer-view-pickup-summary">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={filter}
              onSubmit={onFilterSubmit}
              validationSchema={PickupsLogFilterSchema}
              innerRef={filterFormRef}
            >
              {(props: FormikProps<CustomerViewPickupSummaryFilterListModel>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormSelect
                    name="timePeriod"
                    label="Time Period:"
                    labelMode={LabelModes.column}
                    placeholder="Select..."
                    options={getTimePeriodListValues()}
                    value={getTimePeriodListValues().find(
                      (option) => option.value === props.values.timePeriod
                    )}
                    onValueChange={(option) => handleTimePeriodChange(option ?? null, props)}
                    className="xgs-item-form__field"
                    isClearable
                  />
                  <LabeledDateRangeInput
                    label="Pickup Date Range:"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    start={props.values.from || undefined}
                    end={props.values.to || undefined}
                    maxDate={new Date()}
                    onStartChange={(v) => {
                      props.setFieldValue("from", v || undefined);

                      const newTimePeriod = getTimePeriodFromDates(v || undefined, props.values.to);
                      props.setFieldValue("timePeriod", newTimePeriod);

                      props.submitForm();
                    }}
                    onEndChange={(v) => {
                      props.setFieldValue("to", v || undefined);

                      const newTimePeriod = getTimePeriodFromDates(props.values.from, v || undefined);
                      props.setFieldValue("timePeriod", newTimePeriod);

                      props.submitForm();
                    }}
                  />
                  <LabeledSelectInput
                    label="Requester Company:"
                    labelMode={LabelModes.column}
                    isMulti
                    isClearable
                    value={props.values.requesterCompanyIds || []}
                    options={getOriginTerminalOptions(props.values.requesterCompanyIds || [])}
                    onMultiValuesChange={(option) => {
                      props.setFieldValue("requesterCompanyIds", option ?? []);
                    }}
                    className="xgs-item-form__field"
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    disabled={
                      (pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG"))
                    }
                  >
                    Search
                  </Button>
                  <Button
                    theme={ButtonThemes.gray}
                    onClick={clearSearchRecords}
                  >
                    Clear Filters
                  </Button>
                </Form>
              )
              }
            </Formik >
          </div >
        </div >
        <div className="xgs-list__summary">
          {pickupSummaryTiles && (
            <PicklistsSummary
              picklistSummary={pickupSummaryTiles}
              isLoading={tilesLoading}
            />
          )}
        </div>
        <Table
          isLoading={listLoading}
          columns={columns}
          data={pickupSummaryList?.content || []}
          rowHeight={48}
          minTableHeight={220}
          noResultsText="No Pickup Summary List"
          infiniteScroll
          responsive
        />
        {
          pickupAssignmentState.requestFailed && (
            <XGSErrorMessage className="xgs-pickup-summary__error">{pickupAssignmentState.requestError || "Error while obtaining the log"}</XGSErrorMessage>
          )
        }
      </div >

      {customerViewNumber ?
        <CustomerViewPickupDetails
          requesterCompanyId={customerViewNumber}
          requesterCompany={
            pickupSummaryList?.content.find(
              (t) => t.requesterCompanyId === customerViewNumber
            )?.requesterCompany
          }
          isOpen={!!customerViewNumber}
          onClose={() => setCustomerViewNumber(0)}
          filterData={popupFilterData}
          initialStatus={popupFilterData.status}
        /> : null}
    </>
  );
};

export default CustomerViewPickupSummary;